import {
  type ComponentPropsWithoutRef,
  type ElementRef,
  forwardRef,
  useEffect,
} from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import * as SliderPrimitive from '@radix-ui/react-slider'
import { FormProvider, useForm } from 'react-hook-form'

import {
  type FormType,
  schema,
} from '@/components/ui/range-slider/validation.ts'
import { classNames, formatNumberWithPoints } from '@/utils/helpers.ts'

import { FormField } from '../form/form-field'
import { Separator } from '../separator'
import { SliderThumb } from './SliderThumb.tsx'
import { formatRangeSliderThumbValue, getRawNumber } from './utils'

type Props = ComponentPropsWithoutRef<typeof SliderPrimitive.Root> & {
  label: string
}

export const RangeSlider = forwardRef<
  ElementRef<typeof SliderPrimitive.Root>,
  Props
>(({ className, onValueChange, label, ...props }, ref) => {
  const form = useForm<FormType>({
    resolver: zodResolver(schema),
    defaultValues: {
      min: formatNumberWithPoints(props.value?.[0]),
      max: formatNumberWithPoints(props.value?.[1]),
    },
    mode: 'onChange',
  })

  const { setValue, watch } = form

  const handleChange = (value: number[]) => {
    setValue('min', formatNumberWithPoints(value[0]))
    setValue('max', formatNumberWithPoints(value[1]))
  }
  const [minValue, maxValue] = [watch('min'), watch('max')]

  useEffect(() => {
    onValueChange?.([getRawNumber(minValue), getRawNumber(maxValue)])
  }, [minValue, maxValue, onValueChange])

  const normalizeInputValue = (value: string) => {
    let number = getRawNumber(value || '0')

    if (props.min && number < props.min) {
      number = props.min
    }
    if (props.max && number > props.max) {
      number = props.max
    }

    return formatNumberWithPoints(number)
  }

  return (
    <div className="space-y-3">
      <div className="text-text-default text-xl font-semibold leading-6">
        {label}
      </div>
      <FormProvider {...form}>
        <div className="flex justify-between gap-3">
          <FormField
            formatValue={normalizeInputValue}
            label="Minimum"
            wrapperClassName="flex-1"
            name="min"
          />
          <Separator className="mb-5 w-6 self-end" />
          <FormField
            formatValue={normalizeInputValue}
            label="Maximum"
            wrapperClassName="flex-1"
            name="max"
          />
        </div>
      </FormProvider>
      <SliderPrimitive.Root
        ref={ref}
        className={classNames(
          'relative flex w-full touch-none select-none items-center pt-9',
          className,
        )}
        {...props}
        onValueChange={handleChange}
      >
        <SliderPrimitive.Track className="bg-primary-10 dark:bg-secondary-500 relative h-2 w-full grow overflow-hidden rounded-sm">
          <SliderPrimitive.Range className="bg-primary-500 absolute h-full" />
        </SliderPrimitive.Track>
        <SliderThumb
          value={formatRangeSliderThumbValue(getRawNumber(minValue))}
        />
        <SliderThumb
          value={formatRangeSliderThumbValue(getRawNumber(maxValue))}
        />
      </SliderPrimitive.Root>
    </div>
  )
})

RangeSlider.displayName = SliderPrimitive.Root.displayName
